import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tree, Text, Badge, Icon, Tooltip } from '@oliasoft-open-source/react-ui-library';
import { orderBy, groupBy } from 'lodash';
import {
  addInitialCategory,
  elementSelected,
  reorderElements,
  downloadToExcel,
  openCategoryIdsUpdated,
  duplicateCostItem,
  duplicateCostCategory,
} from 'store/entities/cost-setup/cost-setup';
import { selectOpenCategoryIds } from 'src/store/entities/cost-setup/selector';
import { FiFolder } from 'react-icons/fi';
import { FaRegFolderOpen } from 'react-icons/fa';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { selectCostCodesIdToCodeMap } from 'src/store/entities/cost-codes/selectors';
import { selectPriceBookCompanyItems } from 'src/store/entities/price-book/selectors';
import { selectProjectOverview } from 'store/entities/projects/selectors';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { FeatureFlagName } from 'src/enums/feature-flags';
import { round, unum } from '@oliasoft-open-source/units';
import { meanAllActivities } from './helpers/precalculate-time';
import { selectOperationList } from 'store/entities/activity-model/selectors';
import { getItemTotalCost } from './helpers/get-list-item-total-cost';

const COST_SETUP_SHOW_METRICS_STORAGE_KEY = 'costSetup.showMetrics';

const CostElementsList = ({
  projectId,
  elements,
  isAdding,
  setActiveForm,
  addInitialCategory,
  elementSelected,
  reorderElements,
  setSelectedId,
  setCostItemModalVisible,
  setCategoryModalVisible,
  onClickAddItem,
  isPageDisabled,
  setCategoryRenameModalVisible,
  showImportFromPriceBookModalUpdated,
  costCodesIdToCodeMap,
  priceBookCompanyItems,
  setImportWithLink,
  downloadToExcel,
  projectOverview,
  projectCurrencySettings,
  companyCurrencySettings,
  operations,
  openCategoryIds,
  openCategoryIdsUpdated,
  duplicateCostItem,
  duplicateCostCategory,
}) => {
  const { t } = useTranslation();
  const treeRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { name, wellbore } = projectOverview;

  const [showMetrics, setShowMetrics] = useState(() => {
    try {
      const stored = localStorage.getItem(COST_SETUP_SHOW_METRICS_STORAGE_KEY);
      return stored === null ? true : JSON.parse(stored);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COST_SETUP_SHOW_METRICS_STORAGE_KEY, JSON.stringify(showMetrics));
    } catch (error) {
      console.warn('Failed to persist cost setup showMetrics preference:', error);
    }
  }, [showMetrics]);

  const onClickAddCategory = (parentId = null, costCodesId = null) => {
    addInitialCategory(projectId, t(translations.newCategory), parentId, costCodesId);
    setActiveForm('category');
  };
  const onClickCostItem = (id) => {
    elementSelected(id);
    setActiveForm('item');
  };
  const onClickCategory = (id) => {
    elementSelected(id);
    setActiveForm('category');
  };
  const onClickDeleteItem = (id) => {
    setSelectedId(id);
    setCostItemModalVisible(true);
  };
  const onClickDeleteCategory = (id) => {
    setSelectedId(id);
    setCategoryModalVisible(true);
  };
  const onClickRenameCategory = (id) => {
    elementSelected(id);
    setCategoryRenameModalVisible(true);
  };
  const onClickImportFromPriceBook = ({ id }) => {
    setSelectedId(id);
    showImportFromPriceBookModalUpdated(true);
  };

  const onClickDuplicateItem = (id) => duplicateCostItem(id, projectId);

  const onClickDuplicateCategory = (id) => duplicateCostCategory(id, projectId);

  const handleDownloadExcel = async (projectId) => {
    const fileName = `${name}-cost-setup`;
    await downloadToExcel(projectId, fileName);
  };
  const isProjectCurrencySettingsActive = FeatureFlags.isActive(FeatureFlagName.UserSetCurrency);
  const primaryCurrency = isProjectCurrencySettingsActive
    ? projectCurrencySettings?.primaryCurrency
    : companyCurrencySettings?.currencies?.primaryCurrency;
  const additionalCurrencies = isProjectCurrencySettingsActive
    ? projectCurrencySettings?.additionalCurrencies
    : companyCurrencySettings?.currencies?.additionalCurrencies;

  const currencyRates = useMemo(() => {
    const rates = new Map([[primaryCurrency, 1]]);
    (additionalCurrencies ?? []).forEach(({ currency, exchangeRate }) => {
      rates.set(currency, Number(exchangeRate) || 1);
    });
    return rates;
  }, [primaryCurrency, additionalCurrencies]);

  const convertToPrimary = useCallback(
    (amount, currency) => {
      const rate = currencyRates.get(currency) || 1;
      return amount / rate;
    },
    [currencyRates],
  );

  const categoryTotalCostAndOperations = useMemo(() => {
    const childrenByParent = groupBy(elements, (e) => e.parentId ?? 0);
    const cache = new Map();

    const aggregate = (categoryId) => {
      if (cache.has(categoryId)) return cache.get(categoryId);

      const result = (childrenByParent[categoryId] ?? []).reduce(
        (acc, child) => {
          if (child.costItemId) {
            acc.totalCost += convertToPrimary(getItemTotalCost(child, operations), child.currency);
          } else if (child.costCategoryId) {
            const sub = aggregate(child.costCategoryId);
            acc.totalCost += sub.totalCost;
          }
          return acc;
        },
        { totalCost: 0 },
      );
      result.childCount = (childrenByParent[categoryId] ?? []).length;

      cache.set(categoryId, result);
      return result;
    };

    return elements.reduce((map, el) => {
      if (el.costCategoryId) map.set(el.costCategoryId, aggregate(el.costCategoryId));
      return map;
    }, new Map());
  }, [elements, convertToPrimary, operations]);

  const elementsList = orderBy(elements, 'position').map((item) => {
    const isCategory = !!item.costCategoryId;
    const categoryTotals = isCategory ? categoryTotalCostAndOperations.get(item.costCategoryId) : null;
    const totalCost = isCategory
      ? (categoryTotals?.totalCost ?? 0)
      : convertToPrimary(getItemTotalCost(item, operations), item.currency);
    const counterValue = isCategory ? (categoryTotals?.childCount ?? 0) : (item.operations?.length ?? 0);
    const formattedCost = `${new Intl.NumberFormat('en-US').format(round(totalCost, 2))} ${primaryCurrency || 'USD'}`;
    const isLinkedToPriceBook = !isCategory && !!item?.priceBookItemId && item?.isLinked;
    const isImported = !isCategory && (!!item?.priceBookItemId || !!item?.vendor) && !item?.isLinked;
    const priceBookTooltip = `Price Book${item?.priceBookItemId ? ':' : ''} ${
      priceBookCompanyItems.find((pbItem) => pbItem.priceBookItemId === item.priceBookItemId)?.priceBookName || ''
    }`;

    return {
      testId: `cost-elements-list-category-${convertToKebabCase(item.name)}`,
      id: item.costCategoryId || item.costItemId,
      name: (
        <Text muted={isCategory}>
          {item.name}
          {!!costCodesIdToCodeMap.get(item.costCodesId) && (
            <Text faint small>
              {' '}
              {costCodesIdToCodeMap.get(item.costCodesId)}
            </Text>
          )}
          {isLinkedToPriceBook && (
            <Tooltip
              text={priceBookTooltip}
              display="inline-flex"
              triggerStyle={{ alignItems: 'center', height: '1lh', verticalAlign: 'top', marginLeft: '5px' }}
            >
              <Icon icon="link" />
            </Tooltip>
          )}
          {isImported && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '1lh',
                verticalAlign: 'top',
                marginLeft: '5px',
              }}
            >
              <Icon icon="download" />
            </span>
          )}
        </Text>
      ),
      droppable: !!item.costCategoryId,
      active: item.active,
      parent: item.parentId || 0,
      itemType: item.costCategoryId ? 'category' : 'costItem',
      onClick: () => (item.costCategoryId ? onClickCategory(item.costCategoryId) : onClickCostItem(item.costItemId)),
      actions: [
        ...(showMetrics
          ? [
              {
                childComponent: (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--padding-sm)',
                    }}
                  >
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color: 'var(--color-text-success)',
                      }}
                    >
                      {formattedCost}
                    </span>
                    <Badge title={String(counterValue)} color={counterValue ? '#cccccc' : ''} textColor={'#ffffff'} />
                  </div>
                ),
              },
            ]
          : []),
        ...(item.costCategoryId
          ? [
              {
                testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-more-button`,
                label: t(translations.more),
                subActions: [
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-add-item-option`,
                    label: t(translations.addItem),
                    icon: 'add',
                    onClick: () => onClickAddItem(item.costCategoryId),
                    disabled: isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-add-subcategory-option`,
                    label: t(translations.addSubCategory),
                    icon: 'add',
                    onClick: () => onClickAddCategory(item.costCategoryId, item.costCodesId),
                    disabled: isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-rename-option`,
                    label: t(translations.rename),
                    icon: 'edit',
                    onClick: () => onClickRenameCategory(item.costCategoryId),
                    disabled: isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-duplicate-option`,
                    label: t(translations.duplicate),
                    icon: 'copy',
                    onClick: () => onClickDuplicateCategory(item.costCategoryId),
                    disabled: isAdding || isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(item.name)}-delete-option`,
                    label: t(translations.delete),
                    icon: 'delete',
                    onClick: () => onClickDeleteCategory(item.costCategoryId),
                    disabled: isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(
                      item.name,
                    )}-import-from-price-book-option`,
                    label: t(translations.priceBook_importFromPriceBook),
                    onClick: () => {
                      setImportWithLink(false);
                      onClickImportFromPriceBook({ id: item.costCategoryId });
                    },
                    disabled: isAdding || isPageDisabled,
                    icon: 'download',
                  },
                  {
                    testId: `cost-elements-list-category-${convertToKebabCase(
                      item.name,
                    )}-import-&-link-to-price-book-option`,
                    label: t(translations.priceBook_importAndLinkFromPriceBook),
                    onClick: () => {
                      setImportWithLink(true);
                      onClickImportFromPriceBook({ id: item.costCategoryId });
                    },
                    disabled: isAdding || isPageDisabled,
                    icon: 'link',
                  },
                ],
              },
            ]
          : [
              {
                testId: `cost-elements-list-item-${convertToKebabCase(item.name)}-more-button`,
                label: t(translations.more),
                subActions: [
                  {
                    testId: `cost-elements-list-item-${convertToKebabCase(item.name)}-duplicate-option`,
                    label: t(translations.duplicate),
                    icon: 'copy',
                    onClick: () => onClickDuplicateItem(item.costItemId),
                    disabled: isAdding || isPageDisabled,
                  },
                  {
                    testId: `cost-elements-list-item-${convertToKebabCase(item.name)}-delete-option`,
                    label: t(translations.delete),
                    icon: 'delete',
                    onClick: () => onClickDeleteItem(item.costItemId),
                    disabled: isPageDisabled,
                  },
                ],
              },
            ]),
      ],
    };
  });

  return (
    <Tree
      testId="cost-elements-list"
      treeRef={treeRef}
      icons={{
        expand: <FiFolder />,
        collapse: <FaRegFolderOpen />,
      }}
      itemHasChild={(e) => {
        return elements.some((element) => element.costCategoryId === e.id);
      }}
      list={{
        name: t(translations.settings_costSetup),
        actions: [
          {
            testId: 'cost-elements-list-toggle-metrics-button',
            icon: showMetrics ? <Icon icon="info" color="var(--color-text-success)" /> : 'info',
            tooltip: showMetrics
              ? t(translations.costSetup_hideCostAndCounter)
              : t(translations.costSetup_showCostAndCounter),
            onClick: () => setShowMetrics((prev) => !prev),
          },
          {
            icon: 'download',
            tooltip: t(translations.download),
            onClick: () => handleDownloadExcel(projectId),
            testId: 'cost-elements-list-download-button',
          },
          {
            label: 'Expand/collapse',
            icon: isExpanded ? 'collapse all' : 'expand all',
            onClick: () => {
              if (isExpanded) {
                treeRef.current?.closeAll();
              } else {
                treeRef.current?.openAll();
              }
              setIsExpanded((prev) => !prev);
            },
            disabled: isAdding || isPageDisabled,
          },
          {
            testId: 'cost-elements-list-more-button',
            label: 'Manage',
            subActions: [
              {
                testId: 'cost-elements-list-add-item-option',
                label: t(translations.addItem),
                onClick: () => onClickAddItem(),
                disabled: isAdding || isPageDisabled,
                icon: 'add',
              },
              {
                testId: 'cost-elements-list-add-category-option',
                label: t(translations.addCategory),
                onClick: () => onClickAddCategory(),
                disabled: isAdding || isPageDisabled,
                icon: 'folder',
              },
              {
                testId: 'cost-elements-list-delete-all-costs-option',
                label: t(translations.costSetup_deleteAllCost),
                onClick: () => onClickDeleteItem(null),
                disabled: isAdding || isPageDisabled,
                icon: 'delete',
              },
              {
                testId: 'cost-elements-list-import-from-price-book-option',
                label: t(translations.priceBook_importFromPriceBook),
                onClick: ({ id }) => {
                  onClickImportFromPriceBook({ id });
                  setImportWithLink(false);
                },
                disabled: isAdding || isPageDisabled,
                icon: 'download',
              },
              {
                testId: `cost-elements-list-import-&-link-to-price-book-option`,
                label: t(translations.priceBook_importAndLinkFromPriceBook),
                onClick: ({ id }) => {
                  onClickImportFromPriceBook({ id });
                  setImportWithLink(true);
                },
                disabled: isAdding || isPageDisabled,
                icon: 'link',
              },
            ],
          },
        ],
        items: elementsList,
      }}
      onItemToggle={() => {
        setActiveForm(null);
      }}
      draggable={!isPageDisabled}
      onListReorder={(reordered) => reorderElements(groupBy(reordered, 'parent'), elements)}
      onChangeOpen={openCategoryIdsUpdated}
      isInitialOpen={openCategoryIds}
      stickyHeader
    />
  );
};

const mapStateToProps = (state) => ({
  costCodesIdToCodeMap: selectCostCodesIdToCodeMap(state),
  priceBookCompanyItems: selectPriceBookCompanyItems(state.entities),
  projectOverview: selectProjectOverview(state.entities),
  projectCurrencySettings: state.entities.projectSettings.settings,
  companyCurrencySettings: state.entities.companySettings.settings,
  operations: selectOperationList(state),
  openCategoryIds: selectOpenCategoryIds(state),
});

const mapDispatchToProps = {
  elementSelected,
  addInitialCategory,
  reorderElements,
  downloadToExcel,
  openCategoryIdsUpdated,
  duplicateCostItem,
  duplicateCostCategory,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CostElementsList));

export { Container as CostElementsList };
